import { ArrowRightIcon } from "@radix-ui/react-icons";
import { OpenAPI, OrganizationsService } from "client/openapi";
import { Button, ButtonColor } from "components/Button";
import { useContext, useEffect, useState } from "react";
import IntlTelInput from "intl-tel-input/reactWithUtils";
import "intl-tel-input/build/css/intlTelInput.css";
import { ApiError } from "client/openapi";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import Notifications from "util/notifications";
import { useAuth0 } from "@auth0/auth0-react";

function CollectOrgDetails({ setStep }: { setStep: (step: number) => void }) {
  const { account, updateOrgRolesAndAccount } = useContext(
    OrgRolesAndAccountContext
  );

  const { getAccessTokenSilently } = useAuth0();

  const [orgName, setOrgName] = useState("");
  const [orgEmail, setOrgEmail] = useState("");
  const [orgPhoneNumber, setOrgPhoneNumber] = useState("");

  const [error, setError] = useState<ApiError>();
  const [creatingOrg, setCreatingOrg] = useState(false);
  const [isPhoneValid, setIsPhoneValid] = useState(true);

  const isFormValid = () => {
    return (
      orgName.trim() !== "" &&
      orgEmail.trim() !== "" &&
      orgEmail.includes("@") &&
      orgEmail.includes(".") &&
      (orgPhoneNumber === "" || isPhoneValid)
    );
  };

  async function createOrganization() {
    if (!account) {
      return;
    }

    setCreatingOrg(true);

    try {
      const orgResult = await OrganizationsService.createOrganization({
        requestBody: {
          contact_id: account.reference_id,
          name: orgName,
          public_email: orgEmail,
          public_phone_number: orgPhoneNumber || null,
          billing_features_enabled: true,
          grade_tracking_features_enabled: true,
          payroll_features_enabled: false,
          inactive_student_threshold: 30,
          payments_frozen: false,
        },
      });

      setStep(3);
      const freshToken = await getAccessTokenSilently({ cacheMode: "off" });
      OpenAPI.TOKEN = freshToken;
      updateOrgRolesAndAccount({
        currently_selected_organization: orgResult.id,
      });
      Notifications.success(`Organization ${orgName} created successfully!`);
    } catch (e) {
      setError(e as ApiError);
    } finally {
      setCreatingOrg(false);
    }
  }

  useEffect(() => {
    if (error) {
      Notifications.error(error.body.message);
    }
  }, [error]);

  return (
    <div className="flex flex-col">
      <h1 className="headline-9">Organization Details</h1>

      <div className="mt-2 mb-2 signup_text">
        What are the official details of your organization?
      </div>

      <label htmlFor="org_name" className="mt-4 input-label">
        Organization Name
        <input
          id="org_name"
          name="org_name"
          type="text"
          className="input"
          placeholder="Boston Tutoring, Inc."
          value={orgName}
          onChange={(e) => setOrgName(e.target.value)}
          disabled={creatingOrg}
          required
        />
      </label>

      <label htmlFor="org_email" className="mt-4 input-label">
        Organization Email
        <input
          id="org_email"
          name="org_email"
          type="email"
          className="input"
          placeholder="contact@organization.com"
          value={orgEmail}
          onChange={(e) => setOrgEmail(e.target.value)}
          disabled={creatingOrg}
          required
        />
      </label>

      <label htmlFor="org_phone_number" className="mt-4 input-label">
        Organization Phone Number (recommended)
        <div className="mt-2">
          <IntlTelInput
            inputProps={{
              className: "input mt-2",
              name: "org_phone_number",
            }}
            onChangeValidity={setIsPhoneValid}
            onChangeNumber={(number) => setOrgPhoneNumber(number)}
            initOptions={{
              loadUtils: () => import("intl-tel-input/build/js/utils.js"),
              strictMode: true,
              autoPlaceholder: "aggressive",
              placeholderNumberType: "MOBILE",
              countryOrder: ["us", "gb", "ca"],
              initialCountry: "auto",
              geoIpLookup: (success, failure) => {
                fetch("https://ipapi.co/json/")
                  .then((res) => res.json())
                  .then((data) => success(data.country_code))
                  .catch(() => failure());
              },
              separateDialCode: true,
              nationalMode: true,
            }}
          />
        </div>
      </label>

      <div className="mt-4">
        <Button
          color={!isFormValid() ? ButtonColor.GRAY : ButtonColor.GREEN}
          disabled={creatingOrg || !isFormValid()}
          block={true}
          type="submit"
          onClick={createOrganization}
        >
          {creatingOrg ? (
            <span
              className="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
            ></span>
          ) : (
            <>
              Next <ArrowRightIcon />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

export default CollectOrgDetails;
